import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import {
  Building2,
  Ellipsis,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  SearchX,
  Trash2,
  TriangleAlert,
  Users,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import {
  tenantsCreate,
  tenantsDelete,
  tenantsList,
  tenantsRestore,
  tenantsUpdate,
} from '@/shared/api/generated/tenants/tenants';
import type { TenantBody, TenantsListParams, TenantsListSort } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { useSession } from '@/shared/auth';
import { useDebounced } from '@/shared/lib/useDebounced';
import { Button } from '@/shared/ui/Button';
import {
  DataTable,
  DataTableEmpty,
  DataTablePagination,
  dataTableColumns,
  useDataTable,
} from '@/shared/ui/DataTable';
import { ConfirmDialog } from '@/shared/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { Input } from '@/shared/ui/Input';
import { Status } from '@/shared/ui/Status';
import { toast } from '@/shared/ui/Toast';

import { TenantFormDialog, type TenantFormValues } from './TenantFormDialog';
import { TenantMembersDialog } from './TenantMembersDialog';
import styles from './TenantsPage.module.scss';

/** Поля, по которым сервер умеет сортировать. Тоже из спеки. */
const SORTABLE = new Set<string>(['name', 'slug', 'status', 'created_at', 'updated_at', 'sites']);

const STATUS_TONE = { active: 'success', suspended: 'warning' } as const;
const STATUS_LABEL = { active: 'активен', suspended: 'приостановлен' } as const;

const dateFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

/** Пустой массив один на всё приложение: новый каждый раз пересобирал бы таблицу. */
const NO_TENANTS: TenantBody[] = [];

const helper = dataTableColumns<TenantBody>();

/**
 * Экран тенантов.
 *
 * Таблица работает в «ручном» режиме: сортировку, поиск и страницы считает
 * сервер, а таблица только показывает то, что пришло. Иначе на второй сотне
 * тенантов пришлось бы возить их все в браузер, чтобы отсортировать по
 * названию.
 */
export function TenantsPage() {
  const session = useSession();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [deleted, setDeleted] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TenantBody>();
  const [removing, setRemoving] = useState<TenantBody>();
  const [members, setMembers] = useState<TenantBody>();

  const debouncedSearch = useDebounced(search);

  const params = useMemo<TenantsListParams>(() => {
    const column = sorting[0]?.id;

    return {
      search: debouncedSearch,
      // Поле добавляется только когда задано: явный undefined строгий режим
      // типов считает значением, а не отсутствием.
      ...(deleted && { deleted: true }),
      sort: (column !== undefined && SORTABLE.has(column)
        ? column
        : 'created_at') as TenantsListSort,
      order: sorting[0]?.desc === false ? 'asc' : 'desc',
      page: pagination.pageIndex + 1,
      per_page: pagination.pageSize,
    };
  }, [debouncedSearch, deleted, sorting, pagination]);

  const query = useQuery({
    queryKey: ['tenants', params],
    queryFn: async () => {
      const response = await tenantsList(params);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос списка');
      }

      return response.data;
    },
    // Прежние данные остаются на экране, пока едут новые: без этого таблица
    // мигает пустотой при каждой смене страницы и каждой букве в поиске.
    placeholderData: (previous) => previous,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
  }, [queryClient]);

  const openEdit = useCallback((tenant: TenantBody) => {
    setEditing(tenant);
  }, []);

  const openRemove = useCallback((tenant: TenantBody) => {
    setRemoving(tenant);
  }, []);

  const openMembers = useCallback((tenant: TenantBody) => {
    setMembers(tenant);
  }, []);

  const restore = useCallback(
    (tenant: TenantBody) => {
      tenantsRestore(tenant.id)
        .then(() => {
          toast.success(`Тенант «${tenant.name}» восстановлен`);
          refresh();
        })
        .catch((cause: unknown) => {
          toast.error('Не удалось восстановить', { description: describe(cause) });
        });
    },
    [refresh],
  );

  const save = useMutation({
    mutationFn: async ({
      values,
      id,
      etag,
    }: {
      values: TenantFormValues;
      id?: string | undefined;
      etag?: string | undefined;
    }) => {
      if (id === undefined) {
        await tenantsCreate({ name: values.name, slug: values.slug });

        return;
      }

      await tenantsUpdate(id, { name: values.name, slug: values.slug }, { 'If-Match': etag ?? '' });
    },
    onSuccess: (_data, variables) => {
      toast.success(variables.id === undefined ? 'Тенант создан' : 'Изменения сохранены');
      refresh();
    },
  });

  // Колонки собираются один раз: новый массив на каждой перерисовке заставил
  // бы таблицу пересобирать модель заново.
  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor('name', {
          header: 'Название',
          meta: { name: 'Название' },
          cell: ({ row }) => (
            <span className={styles['cell']}>
              <span className={styles['cell__title']}>{row.original.name}</span>
              <span className={styles['cell__sub']}>{row.original.slug}</span>
            </span>
          ),
        }),

        helper.accessor('status', {
          header: 'Состояние',
          meta: { name: 'Состояние' },
          cell: ({ getValue }) => {
            const status = getValue();

            return <Status tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Status>;
          },
        }),

        helper.accessor('sites_count', {
          id: 'sites',
          header: 'Сайтов',
          meta: { align: 'end', name: 'Сайтов' },
          cell: ({ getValue }) => <span className={styles['cell__number']}>{getValue()}</span>,
        }),

        helper.accessor('created_at', {
          header: 'Заведён',
          meta: { name: 'Заведён' },
          cell: ({ getValue }) => (
            <span className={styles['cell__sub']}>{dateFormat.format(new Date(getValue()))}</span>
          ),
        }),

        helper.display({
          id: 'actions',
          meta: { align: 'end', width: '56px', name: 'Действия' },
          enableHiding: false,
          header: () => <span className={styles['hidden-label']}>Действия</span>,
          cell: ({ row }) => (
            <RowActions
              tenant={row.original}
              onEdit={openEdit}
              onMembers={openMembers}
              onRemove={openRemove}
              onRestore={restore}
            />
          ),
        }),
      ]),
    [openEdit, openMembers, openRemove, restore],
  );

  const table = useDataTable({
    columns,
    data: query.data?.items ?? NO_TENANTS,
    getRowId: (tenant) => tenant.id,
    // Ручной режим: сервер уже отдал нужную страницу в нужном порядке, и
    // таблице нельзя сортировать и резать её повторно.
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: query.data?.total ?? 0,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
  });

  if (session.status === 'authenticated' && !session.user.is_super_admin) {
    return (
      <p className={styles['note']}>Управление тенантами доступно только владельцу платформы.</p>
    );
  }

  const narrowed = debouncedSearch !== '';

  return (
    <div className={styles['page']}>
      <header className={styles['head']}>
        <div>
          <h1 className={styles['title']}>Тенанты</h1>
          <p className={styles['subtitle']}>Клиенты платформы. У каждого свои сайты и люди.</p>
        </div>

        <Button
          iconLeft={<Plus size={15} />}
          onClick={() => {
            setCreating(true);
          }}
        >
          Создать тенанта
        </Button>
      </header>

      <div className={styles['toolbar']}>
        <Input
          className={styles['toolbar__search']}
          placeholder="Поиск по названию и короткому имени"
          aria-label="Поиск по тенантам"
          prefix={<Search size={15} />}
          clearable
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
        />

        <Button
          variant={deleted ? 'solid' : 'outline'}
          tone={deleted ? 'accent' : 'neutral'}
          iconLeft={<Trash2 size={15} />}
          onClick={() => {
            setDeleted((prev) => !prev);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
        >
          Корзина
        </Button>
      </div>

      <DataTable
        table={table}
        caption="Тенанты платформы"
        loading={query.isLoading}
        refreshing={query.isFetching && !query.isLoading}
        empty={emptyState({
          error: query.isError,
          narrowed,
          deleted,
          onRetry: () => {
            void query.refetch();
          },
          onReset: () => {
            setSearch('');
          },
          onCreate: () => {
            setCreating(true);
          },
        })}
      />

      <DataTablePagination table={table} />

      <TenantFormDialog
        open={creating}
        onOpenChange={setCreating}
        onSubmit={async (values) => {
          await save.mutateAsync({ values });
        }}
      />

      <TenantFormDialog
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(undefined);
          }
        }}
        tenant={editing}
        onSubmit={async (values) => {
          await save.mutateAsync({ values, id: editing?.id, etag: editing?.etag });
        }}
      />

      <TenantMembersDialog
        tenant={members}
        onOpenChange={(open) => {
          if (!open) {
            setMembers(undefined);
          }
        }}
      />

      <ConfirmDialog
        open={removing !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(undefined);
          }
        }}
        tone="danger"
        title={`Удалить «${removing?.name ?? ''}»?`}
        description={
          removing !== undefined && removing.sites_count > 0
            ? `Вместе с тенантом в корзину уедут его сайты: ${String(removing.sites_count)}. Восстановить можно из корзины.`
            : 'Тенант отправится в корзину. Восстановить можно оттуда же.'
        }
        confirmLabel="Удалить"
        onConfirm={async () => {
          if (removing === undefined) {
            return;
          }

          await tenantsDelete(removing.id);
          toast.success(`Тенант «${removing.name}» в корзине`, {
            action: {
              label: 'Вернуть',
              altText: 'Восстановить тенанта можно в корзине',
              onClick: () => {
                restore(removing);
              },
            },
          });
          refresh();
        }}
      />
    </div>
  );
}

function RowActions({
  tenant,
  onEdit,
  onMembers,
  onRemove,
  onRestore,
}: {
  tenant: TenantBody;
  onEdit: (tenant: TenantBody) => void;
  onMembers: (tenant: TenantBody) => void;
  onRemove: (tenant: TenantBody) => void;
  onRestore: (tenant: TenantBody) => void;
}) {
  const inTrash = tenant.deleted_at !== undefined && tenant.deleted_at !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          iconOnly
          variant="ghost"
          tone="neutral"
          size="sm"
          aria-label={`Действия с тенантом «${tenant.name}»`}
        >
          <Ellipsis size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {inTrash ? (
          <DropdownMenuItem
            icon={<RotateCcw size={15} />}
            onSelect={() => {
              onRestore(tenant);
            }}
          >
            Восстановить
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem
              icon={<Users size={15} />}
              onSelect={() => {
                onMembers(tenant);
              }}
            >
              Люди и роли
            </DropdownMenuItem>

            <DropdownMenuItem
              icon={<Pencil size={15} />}
              onSelect={() => {
                onEdit(tenant);
              }}
            >
              Изменить
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              tone="danger"
              icon={<Trash2 size={15} />}
              onSelect={() => {
                onRemove(tenant);
              }}
            >
              Удалить
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Чем занять таблицу, когда строк нет.
 *
 * Случаев четыре, и они не взаимозаменяемы: ошибка зовёт повторить, узкий
 * фильтр — сбросить, пустая корзина ничего не требует, а пустой список
 * приглашает завести первого клиента.
 */
function emptyState({
  error,
  narrowed,
  deleted,
  onRetry,
  onReset,
  onCreate,
}: {
  error: boolean;
  narrowed: boolean;
  deleted: boolean;
  onRetry: () => void;
  onReset: () => void;
  onCreate: () => void;
}) {
  if (error) {
    return (
      <DataTableEmpty
        icon={<TriangleAlert size={20} />}
        title="Не удалось загрузить список"
        description="Сервер не ответил. Данные не потеряны — попробуйте ещё раз."
        action={
          <Button variant="outline" tone="neutral" onClick={onRetry}>
            Повторить
          </Button>
        }
      />
    );
  }

  if (narrowed) {
    return (
      <DataTableEmpty
        icon={<SearchX size={20} />}
        title="Ничего не найдено"
        description="Под текущий поиск и фильтры не подходит ни один тенант."
        action={
          <Button variant="outline" tone="neutral" onClick={onReset}>
            Сбросить фильтры
          </Button>
        }
      />
    );
  }

  if (deleted) {
    return <DataTableEmpty icon={<Trash2 size={20} />} title="Корзина пуста" />;
  }

  return (
    <DataTableEmpty
      icon={<Building2 size={20} />}
      title="Пока нет ни одного тенанта"
      description="Тенант — это клиент платформы. У него свои сайты и люди."
      action={
        <Button iconLeft={<Plus size={15} />} onClick={onCreate}>
          Создать первого
        </Button>
      }
    />
  );
}

function describe(cause: unknown): string {
  return cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.';
}
